import React, { useMemo } from "react";
import { Card, Tag, Tooltip, Badge, message } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import {
  colors,
  getSupplierInitials,
  formatMYR,
  getSupplierGrandTotal,
  getSupplierTaxableTotal,
  MaterialInfo,
} from "./comparisonUtils";

interface SupplierCardStripProps {
  suppliers: any[];
  evaluationScoreMap: Record<string, any>;
  materialNames: MaterialInfo[];
  selectedSupplierIDs: string[];
  onSelectionChange: (ids: string[]) => void;
  shortlistedSupplierIDs: string[];
  sortBy: "rank" | "price" | "score";
}

const MAX_SELECTED = 5;

const SupplierCardStrip: React.FC<SupplierCardStripProps> = ({
  suppliers,
  evaluationScoreMap,
  materialNames,
  selectedSupplierIDs,
  onSelectionChange,
  shortlistedSupplierIDs,
  sortBy,
}) => {
  const sortedSuppliers = useMemo(() => {
    const copy = [...suppliers];
    switch (sortBy) {
      case "rank":
        return copy.sort((a, b) => {
          const rankA = evaluationScoreMap[a.supplierID]?.rank ?? 999;
          const rankB = evaluationScoreMap[b.supplierID]?.rank ?? 999;
          return rankA - rankB;
        });
      case "price":
        return copy.sort((a, b) => {
          const totalA = getSupplierGrandTotal(a, materialNames);
          const totalB = getSupplierGrandTotal(b, materialNames);
          return totalA - totalB;
        });
      case "score":
        return copy.sort((a, b) => {
          const scoreA = evaluationScoreMap[a.supplierID]?.overallScore ?? 0;
          const scoreB = evaluationScoreMap[b.supplierID]?.overallScore ?? 0;
          return scoreB - scoreA;
        });
      default:
        return copy;
    }
  }, [suppliers, evaluationScoreMap, materialNames, sortBy]);

  const handleCardClick = (supplierID: string) => {
    if (selectedSupplierIDs.includes(supplierID)) {
      onSelectionChange(selectedSupplierIDs.filter((id) => id !== supplierID));
    } else {
      if (selectedSupplierIDs.length >= MAX_SELECTED) {
        message.warning(
          "Maximum 5 suppliers can be compared at once"
        );
        return;
      }
      onSelectionChange([...selectedSupplierIDs, supplierID]);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        overflowX: "auto",
        gap: 12,
        padding: "12px 0",
      }}
    >
      {sortedSuppliers.map((supplier, index) => {
        const evalData = evaluationScoreMap[supplier.supplierID];
        const isSelected = selectedSupplierIDs.includes(supplier.supplierID);
        const isShortlisted = shortlistedSupplierIDs.includes(
          supplier.supplierID
        );
        const taxableTotal = getSupplierTaxableTotal(supplier, materialNames);
        const colorIndex = suppliers.findIndex(
          (s) => s.supplierID === supplier.supplierID
        );

        const rankColor =
          evalData?.rank === 1
            ? "gold"
            : evalData?.rank === 2
            ? "default"
            : evalData?.rank === 3
            ? "orange"
            : "blue";

        const cardContent = (
          <Card
            hoverable
            size="small"
            onClick={() => handleCardClick(supplier.supplierID)}
            style={{
              width: 180,
              minWidth: 180,
              cursor: "pointer",
              border: isSelected
                ? "2px solid #1890ff"
                : "1px solid #f0f0f0",
              borderRadius: 8,
              position: "relative",
              transition: "all 0.2s",
            }}
            bodyStyle={{ padding: 12 }}
          >
            {isSelected && (
              <div
                style={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  backgroundColor: "#1890ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CheckOutlined
                  style={{ color: "#fff", fontSize: 11 }}
                />
              </div>
            )}

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 6,
              }}
            >
              {/* Avatar */}
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: colors[colorIndex % colors.length],
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {getSupplierInitials(supplier.supplierName)}
              </div>

              {/* Rank Badge */}
              {evalData && (
                <Tag
                  color={rankColor}
                  style={{ fontSize: 11, margin: 0 }}
                >
                  Rank #{evalData.rank}
                </Tag>
              )}

              {/* Supplier Name */}
              <Tooltip title={supplier.supplierName}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    textAlign: "center",
                    width: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {supplier.supplierName}
                </div>
              </Tooltip>

              {/* Overall Score */}
              {evalData && (
                <div style={{ fontSize: 11, color: "#666" }}>
                  Score: {evalData.overallScore.toFixed(1)}/100
                </div>
              )}

              {/* Taxable Amount */}
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#1890ff",
                }}
              >
                INR {formatMYR(taxableTotal)}
              </div>

              {/* Payment Terms */}
              <div style={{ fontSize: 11, color: "#999" }}>
                {supplier.paymentTerms}
              </div>
            </div>
          </Card>
        );

        if (isShortlisted) {
          return (
            <Badge.Ribbon
              key={supplier.supplierID}
              text="Shortlisted"
              color="green"
              style={{ fontSize: 10 }}
            >
              {cardContent}
            </Badge.Ribbon>
          );
        }

        return (
          <React.Fragment key={supplier.supplierID}>
            {cardContent}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default SupplierCardStrip;
